import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { CallState, CallSession, CallDisposition, CallError } from './call-types';

/**
 * Event streaming service for call-related events
 * Provides observables for reactive programming patterns
 */

export interface CallStateChange {
  from: CallState;
  to: CallState;
  session?: CallSession;
  timestamp: Date;
}

export interface CallEndEvent {
  session: CallSession;
  disposition: CallDisposition;
  timestamp: Date;
}

export interface CallDispositionUpdate {
  callId: string;
  disposition: CallDisposition;
  notes?: string;
  scheduledFollowUp?: Date;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class CallEventsService {
  // Event subjects
  private stateChangeSubject = new Subject<CallStateChange>();
  private callStartedSubject = new Subject<CallSession>();
  private callEndedSubject = new Subject<CallEndEvent>();
  private dispositionSubject = new Subject<CallDispositionUpdate>();
  private errorSubject = new Subject<CallError>();

  /**
   * Observable for state changes
   */
  onStateChange(): Observable<CallStateChange> {
    return this.stateChangeSubject.asObservable();
  }

  /**
   * Observable for call started events
   */
  onCallStarted(): Observable<CallSession> {
    return this.callStartedSubject.asObservable();
  }

  /**
   * Observable for call ended events
   */
  onCallEnded(): Observable<CallEndEvent> {
    return this.callEndedSubject.asObservable();
  }

  /**
   * Observable for disposition updates
   */
  onDispositionUpdate(): Observable<CallDispositionUpdate> {
    return this.dispositionSubject.asObservable();
  }

  /**
   * Observable for call errors
   */
  onError(): Observable<CallError> {
    return this.errorSubject.asObservable();
  }

  /**
   * Emit state change event
   * Package-private - only call services should use
   */
  emitStateChange(from: CallState, to: CallState, session?: CallSession): void {
    this.stateChangeSubject.next({
      from,
      to,
      session,
      timestamp: new Date()
    });
  }

  /**
   * Emit call started event
   */
  emitCallStarted(session: CallSession): void {
    this.callStartedSubject.next(session);
  }

  /**
   * Emit call ended event
   */
  emitCallEnded(session: CallSession, disposition: CallDisposition): void {
    this.callEndedSubject.next({
      session,
      disposition,
      timestamp: new Date()
    });
  }

  /**
   * Emit disposition update event
   */
  emitDispositionUpdate(callId: string, disposition: CallDisposition, notes?: string): void {
    this.dispositionSubject.next({
      callId,
      disposition,
      notes,
      timestamp: new Date()
    });
  }

  /**
   * Emit error event
   */
  emitError(error: CallError): void {
    this.errorSubject.next(error);
  }
}
